import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { NgClass } from '@angular/common';
import {
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
  FeatureFlags,
  MERCURY_TEST_COMPANY_ID,
  StorageKeys,
} from '@core/constants';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { BackButtonService } from '@core/services/back-button/back-button.service';
import { CustomerService } from '@core/services/customer/customer.service';
import { EnvironmentService } from '@core/services/environment.service';
import { FeatureFlagService } from '@core/services/feature-flag/feature-flag.service';
import { FrontChatService } from '@core/services/front-chat/front-chat.service';
import { StorageService } from '@core/services/storage/storage.service';
import { Logout } from '@core/state/auth/auth.actions';
import {
  selectUserFullName,
  selectUserProfile,
} from '@core/state/userProfile/userProfile.selectors';
import { getNameCharAsIcon } from '@core/utils/stringUtil';
import { EnvironmentNameEnum } from '@environments/environment.interface';
import { Store } from '@ngrx/store';
import { MenuItem } from 'primeng/api';
import { ButtonDirective, ButtonIcon } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { Tooltip } from 'primeng/tooltip';
import {
  filter,
  map,
  mergeMap,
  Observable,
  startWith,
  Subject,
  takeUntil,
} from 'rxjs';
import { IUserProfileModel } from 'src/app/modules/user-profile/features/user-profile-page/models/user-profile.model';

import { ChangePasswordComponent } from '../change-password/change-password.component';
import { HeaderMenuComponent } from '../header-menu/header-menu.component';
import { SupportComponent } from '../support/support.component';

@Component({
  selector: 'au-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    ButtonDirective,
    NgClass,
    MenuModule,
    ChangePasswordComponent,
    SupportComponent,
    HeaderMenuComponent,
    Tooltip,
    ButtonIcon,
  ],
})
export class HeaderComponent implements OnInit, OnDestroy {
  items: MenuItem[] = [];
  userAvatar = '';
  title = '';
  isBackButton: boolean = false;
  destroyed$: Subject<void> = new Subject();
  showChangePassword: boolean = false;
  showContactSupport: boolean = false;
  userFullName$: Observable<string>;
  userProfile$: Observable<IUserProfileModel | null>;
  showOnlyDesktop: boolean = false;
  isChatButtonVisible: boolean = false;
  squadName: string = 'Us';
  squadPicturePath: string = '../../../assets/images/profiles/profile_init.png';
  squadInfo: any = null;
  currentUserProfile: IUserProfileModel | null = null;
  showSandboxProfilePanel: boolean = false;
  private chatLoadId = 0;

  responsiveObserver = inject(BreakpointObserver);

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private store: Store<any>,
    private authService: AuthenticationService,
    private storageService: StorageService,
    private backButtonService: BackButtonService,
    private featureFlagService: FeatureFlagService,
    private envService: EnvironmentService,
    private customerService: CustomerService,
    private frontChatService: FrontChatService
  ) {
    this.userFullName$ = this.store.select(selectUserFullName);
    this.userProfile$ = this.store.select(selectUserProfile);
  }

  ngOnInit() {
    this.getSquadInfo();

    this.userProfile$.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
      this.currentUserProfile = value;
      this.updateSandboxProfilePanelVisibility();
      this.userAvatar =
        getNameCharAsIcon(value?.firstName) +
        getNameCharAsIcon(value?.lastName);
      this.loadChat(value);
    });

    this.items = [
      {
        label: 'Profile Settings',
        icon: 'icon-bx-id-card',
        command: (_event) => {
          this.router.navigateByUrl('user-profile');
        },
      },
      {
        label: 'Update Password',
        icon: 'icon-bx-lock-alt',
        command: (_event) => {
          this.showChangePassword = true;
        },
      },
      {
        label: 'Log Out',
        icon: 'icon-bx-log-out-circle',
        command: (_event) => {
          this.logout();
        },
      },
    ];

    this.routerEvents();

    this.responsiveObserver
      .observe([Breakpoints.Small, Breakpoints.XSmall])
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result) => {
        this.showOnlyDesktop = !result.matches;
        this.updateSandboxProfilePanelVisibility();
      });
  }

  updateSandboxProfilePanelVisibility(): void {
    this.showSandboxProfilePanel =
      this.envService.name === EnvironmentNameEnum.TEST &&
      this.showOnlyDesktop &&
      !!this.currentUserProfile;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    const launcher = document.querySelector('#chat-launcher-btn');
    const chatIframe = document.querySelector('#front-chat-iframe');

    const clickedLauncher =
      launcher && (target === launcher || launcher?.contains(target));

    const clickedIframe =
      chatIframe && (target === chatIframe || chatIframe?.contains(target));

    if (clickedLauncher || clickedIframe) {
      return;
    }

    if (this.isChatButtonVisible) {
      // @ts-ignore
      document.defaultView?.FrontChat('hide');
    }
  }

  openChatWidget(event?: MouseEvent) {
    event?.stopPropagation();
    // @ts-ignore
    document.defaultView?.FrontChat('show');
  }

  toggleChatVisibility(isChatVisible: boolean) {
    this.isChatButtonVisible = isChatVisible;

    const chatIframe = document.querySelector('#front-chat-iframe');
    if (chatIframe) {
      chatIframe.classList.toggle('hidden', !isChatVisible);
      chatIframe.classList.toggle('block', isChatVisible);
    }
  }

  loadChat(value: any): void {
    if (!value) return;

    const squadID = value.customer?.squadId;
    if (!squadID) return;

    const chatID = this.envService.chatbotSetting.find(
      (e: any) => e.squadID === squadID
    )?.chatID;
    if (!chatID) return;

    const loadId = ++this.chatLoadId;
    this.frontChatService.reset();
    document.querySelector('#front-chat-script')?.remove();
    document.querySelector('#front-chat-iframe')?.remove();

    const script = document.createElement('script');
    script.id = 'front-chat-script';
    script.src = 'https://chat-assets.frontapp.com/v1/chat.bundle.js';
    document.body.appendChild(script);

    script.onload = () => {
      if (loadId !== this.chatLoadId) return;
      // @ts-ignore
      document.defaultView?.FrontChat('init', {
        chatId: chatID,
        useDefaultLauncher: false,
        name: `${value.firstName} ${value.lastName} (${value.customer.id}:${value.customer.name})`,
        onInitCompleted: () => {
          if (loadId === this.chatLoadId) {
            this.frontChatService.markInitialized();
          }
        },
      });

      this.toggleChatVisibility(
        this.featureFlagService.featureFlagEnabled(
          FeatureFlags.SHOW_LIVE_CHAT
        ) || value.customer?.id === MERCURY_TEST_COMPANY_ID
      );
    };
  }

  getSquadInfo() {
    this.customerService
      .getSquadInfo()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((res) => {
        this.squadInfo = res.data;
        this.squadName = res.data?.name?.split(' ')[0] || '';

        switch (res.data?.id) {
          case 1: // Tango Squad - Nelson
            this.squadPicturePath =
              '../../../assets/images/profiles/profile_nelson.png';
            break;
          case 2: // Echo Squad - Abigail
            this.squadPicturePath =
              '../../../assets/images/profiles/profile_abigail.png';
            break;
          case 4: // Foxtrot Squad - Tony
            this.squadPicturePath =
              '../../../assets/images/profiles/profile_tony.png';
            break;
          case 6: // Delta Squad - Tony
            this.squadPicturePath =
              '../../../assets/images/profiles/profile_default.png';
            break;
          case 7: // Bravo Squad - Lukas
            this.squadPicturePath =
              '../../../assets/images/profiles/profile_lukas.png';
            break;
          case 8: // Alpha Squad - Zac
            this.squadPicturePath =
              '../../../assets/images/profiles/profile_zac.png';
            break;
          case 9: // Charlie Squad - Scott
            this.squadPicturePath =
              '../../../assets/images/profiles/profile_scott.png';
            break;
          case 10: // Sierra - Tony
            this.squadPicturePath =
              '../../../assets/images/profiles/profile_default.png';
            break;
          case 11: // Redwood Squad - James
            this.squadPicturePath =
              '../../../assets/images/profiles/profile_default.png';
            break;
          case 12: // Kilo Squad - Tobey
            this.squadPicturePath =
              '../../../assets/images/profiles/profile_default.png';
            break;
          case 13: // Polaris Squad - Rachel
            this.squadPicturePath =
              '../../../assets/images/profiles/profile_default.png';
            break;
          default:
            this.squadPicturePath =
              '../../../assets/images/profiles/profile_default.png';
            break;
        }
      });
  }

  routerEvents() {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        startWith(this.router),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
        mergeMap((route) => route.data),
        takeUntil(this.destroyed$)
      )
      .subscribe(({ headerTitle, isBackButton }) => {
        this.title = headerTitle;
        this.isBackButton = isBackButton;
      });
  }

  cancelChangePassword = () => {
    this.showChangePassword = false;
  };

  openContactSupport = () => {
    this.showContactSupport = true;
  };

  sendEmail() {
    if (this.squadInfo?.email) {
      window.open(`mailto:${this.squadInfo?.email}`, '_self');
    }
  }

  callNumber() {
    const phone = this.squadInfo?.phone;
    if (phone?.phoneCountryCode && phone?.phoneNumber) {
      const fullNumber = `+${phone.phoneCountryCode}${phone.phoneNumber}`;
      window.open(`tel:${fullNumber}`, '_self');
    }
  }

  logout() {
    this.authService
      .logout()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.store.dispatch(Logout());
        this.storageService.delete(StorageKeys.authUser);
        // @ts-ignore
        document.defaultView?.FrontChat('shutdown', { clearSession: true });
      });
  }

  goBack(): void {
    this.backButtonService.goBack();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.unsubscribe();
  }
}
